import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Heart } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { getCollections } from '../services/collectionService';
import { getProducts } from '../services/productService';

const CollectionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { formatMoney, getProductPrice } = useCurrency();
  const { wishlistIds, toggleWishlist } = useWishlist();

  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const allCollections = getCollections();
        const found = (allCollections || []).find((c) => String(c?.slug) === String(slug));
        setCollection(found || null);

        const allProducts = await getProducts();
        setProducts(Array.isArray(allProducts) ? allProducts : []);
      } catch {
        setCollection(null);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [slug]);

  const collectionProducts = useMemo(() => {
    const ids = new Set((collection?.productIds || []).map(String));
    return (products || []).filter((p) => ids.has(String(p?.id)));
  }, [collection?.productIds, products]);

  const onToggleWishlist = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleWishlist(productId);
    } catch {
      // ignore
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500">Loading collection...</p>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500 mb-4">Collection not found</p>
        <button onClick={() => navigate('/shop/new-arrivals')} className="text-sm underline hover:opacity-60">
          Back to shop
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm mb-8 hover:opacity-60 transition-opacity"
      >
        <ChevronLeft size={16} />
        Back
      </button>

      <div className="mb-10">
        <h1 className="text-2xl font-medium tracking-wide mb-2">{collection.name}</h1>
        {collection.description ? <p className="text-sm text-gray-600">{collection.description}</p> : null}
      </div>

      {collectionProducts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-500 mb-4">No products in this collection yet.</p>
          <Link to="/shop/new-arrivals" className="text-sm underline hover:opacity-60">
            Browse all products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {collectionProducts.map((product) => (
            <Link key={product.id} to={`/product/${product.id}`} className="group">
              <div className="aspect-square bg-gray-100 mb-3 overflow-hidden relative">
                <img
                  src={product.images?.[0]}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <button
                  onClick={(e) => onToggleWishlist(e, product.id)}
                  className={`absolute top-2 right-2 w-9 h-9 flex items-center justify-center border transition-colors ${
                    wishlistIds.includes(String(product.id))
                      ? 'border-black bg-black text-white'
                      : 'border-gray-200 bg-white/90 hover:bg-white hover:border-black'
                  }`}
                  aria-label="Toggle wishlist"
                >
                  <Heart size={16} />
                </button>
                {!product.inStock && (
                  <div className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1">SOLD OUT</div>
                )}
              </div>
              <h3 className="text-sm font-medium mb-1">{product.name}</h3>
              <p className="text-sm">{formatMoney(getProductPrice(product))}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default CollectionDetail;
